import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Calendar, ChevronLeft, ChevronRight, Plus, Edit3, Trash2, Save, X, Repeat, Landmark, AlertCircle } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const DAYS_FULL = ["Lunedi", "Martedi", "Mercoledi", "Giovedi", "Venerdi", "Sabato", "Domenica"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function getWeekDates(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(monday);
    dt.setDate(monday.getDate() + i);
    return dt;
  });
}

function formatDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// Get day-of-week index (0=Mon, 6=Sun) from a Date
function getDayIndex(date) {
  const d = date.getDay();
  return d === 0 ? 6 : d - 1;
}

const EVENT_COLORS = [
  { value: "#2FD8A8", label: "Oro" },
  { value: "#64C8FF", label: "Azzurro" },
  { value: "#E1306C", label: "Rosa" },
  { value: "#10B981", label: "Verde" },
  { value: "#F7931A", label: "Arancio" },
  { value: "#A855F7", label: "Viola" },
  { value: "#EF4444", label: "Rosso" },
  { value: "#FFFFFF", label: "Bianco" },
];

const RECURRENCE_OPTIONS = [
  { value: "none", label: "UNA VOLTA" },
  { value: "weekly", label: "SETTIMANALE" },
  { value: "biweekly", label: "OGNI 2 SETTIMANE" },
  { value: "monthly", label: "MENSILE" },
];

function parseDateKey(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function daysBetween(a, b) {
  return Math.round((b - a) / 86400000);
}

// Normalize old data model (recurring boolean) to recurrence string
function getRecurrence(ev) {
  if (ev.recurrence) return ev.recurrence;
  return ev.recurring ? "weekly" : "none";
}

export default function CalendarWidget() {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", educator: "", date: "", time: "", recurrence: "none", dayOfWeek: 0, color: "#2FD8A8" });

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);

  const [econEvents, setEconEvents] = useState([]);
  const [econPopup, setEconPopup] = useState(null);

  useEffect(() => {
    const start = formatDateKey(weekDates[0]);
    const end = formatDateKey(weekDates[6]);
    const todayKey = formatDateKey(new Date());
    const isCurrentWeek = todayKey >= start && todayKey <= end;
    (async () => {
      try {
        const res = isCurrentWeek
          ? await axios.get(`${API}/economic-calendar`)
          : await axios.get(`${API}/economic-calendar/range`, { params: { start, end } });
        const evs = (res.data.events || []).filter(
          (e) => e.country === "USD" && (e.impact === "High" || e.impact === "Holiday")
        );
        setEconEvents(evs);
      } catch { setEconEvents([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const getEconForDay = (dateKey) =>
    econEvents.filter((e) => {
      try { return formatDateKey(new Date(e.date)) === dateKey; } catch { return false; }
    });

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/content/calendar-events`);
      setEvents(res.data.map(e => {
        try { return { ...e, ...JSON.parse(e.content) }; }
        catch { return e; }
      }));
    } catch (e) { console.error(e); }
  };

  // Build map: dateKey -> [events] including recurring ones
  const getEventsForDay = (dateKey, dayIndex) => {
    const dayEvents = [];
    const cellDate = parseDateKey(dateKey);
    events.forEach(ev => {
      const rec = getRecurrence(ev);
      if (rec === "weekly" && ev.dayOfWeek === dayIndex) {
        dayEvents.push(ev);
      } else if (rec === "biweekly" && ev.date) {
        const anchor = parseDateKey(ev.date);
        const diff = daysBetween(anchor, cellDate);
        if (diff >= 0 && diff % 14 === 0) dayEvents.push(ev);
      } else if (rec === "monthly" && ev.date) {
        const anchor = parseDateKey(ev.date);
        if (cellDate >= anchor && cellDate.getDate() === anchor.getDate()) dayEvents.push(ev);
      } else if (rec === "none" && ev.date === dateKey) {
        dayEvents.push(ev);
      }
    });
    dayEvents.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return dayEvents;
  };

  const prevWeek = () => { const d = new Date(currentDate); d.setDate(d.getDate() - 7); setCurrentDate(d); };
  const nextWeek = () => { const d = new Date(currentDate); d.setDate(d.getDate() + 7); setCurrentDate(d); };
  const goToday = () => setCurrentDate(new Date());

  const openAdd = (dateStr, dayIdx) => {
    setForm({ title: "", educator: "", date: dateStr || formatDateKey(new Date()), time: "", recurrence: "none", dayOfWeek: dayIdx ?? 0, color: "#2FD8A8" });
    setShowAdd(true);
    setEditingId(null);
  };

  const startEdit = (ev, e) => {
    e.stopPropagation();
    setEditingId(ev.id);
    setForm({
      title: ev.title,
      educator: ev.educator || "",
      date: ev.date || "",
      time: ev.time || "",
      recurrence: getRecurrence(ev),
      dayOfWeek: ev.dayOfWeek ?? 0,
      color: ev.color || "#2FD8A8"
    });
    setShowAdd(false);
  };

  const saveEvent = async () => {
    if (!form.title.trim()) { toast.error("Inserisci il nome della call"); return; }
    if (form.recurrence !== "weekly" && !form.date) { toast.error("Inserisci una data"); return; }
    try {
      const payload = {
        educator: form.educator,
        time: form.time,
        recurrence: form.recurrence,
        color: form.color,
        dayOfWeek: form.recurrence === "weekly" ? form.dayOfWeek : undefined,
        date: form.recurrence === "weekly" ? undefined : form.date
      };
      if (editingId) {
        await axios.put(`${API}/content/${editingId}`, {
          title: form.title,
          content: JSON.stringify(payload)
        });
        toast.success("Evento aggiornato");
      } else {
        await axios.post(`${API}/content`, {
          section: "calendar-events",
          content_type: "event",
          title: form.title,
          content: JSON.stringify(payload),
          order: 0
        });
        toast.success(form.recurrence !== "none" ? "Call ricorrente aggiunta" : "Evento aggiunto");
      }
      setShowAdd(false);
      setEditingId(null);
      setForm({ title: "", educator: "", date: "", time: "", recurrence: "none", dayOfWeek: 0, color: "#2FD8A8" });
      fetchEvents();
    } catch { toast.error("Errore"); }
  };

  const deleteEvent = async (id, e) => {
    e.stopPropagation();
    try { await axios.delete(`${API}/content/${id}`); toast.success("Eliminato"); fetchEvents(); }
    catch { toast.error("Errore"); }
  };

  const today = formatDateKey(new Date());
  const weekStart = weekDates[0];
  const weekEnd = weekDates[6];
  const headerText = weekStart.getMonth() === weekEnd.getMonth()
    ? `${weekStart.getDate()} - ${weekEnd.getDate()} ${MONTHS[weekStart.getMonth()]} ${weekStart.getFullYear()}`
    : `${weekStart.getDate()} ${MONTHS[weekStart.getMonth()].slice(0, 3)} - ${weekEnd.getDate()} ${MONTHS[weekEnd.getMonth()].slice(0, 3)} ${weekStart.getFullYear()}`;

  return (
    <div className="glass-card p-4 sm:p-6 mb-8" data-testid="calendar-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Calendar size={22} style={{ color: "var(--mm-gold)" }} />
          <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>CALL SETTIMANALI</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevWeek} className="p-2 rounded-lg transition-colors hover:bg-white/10" data-testid="cal-prev"><ChevronLeft size={20} style={{ color: "var(--mm-text-secondary)" }} /></button>
          <button onClick={goToday} className="p-2 rounded-lg transition-colors hover:bg-white/10" title="Torna a oggi" data-testid="cal-today">
            <div className="w-5 h-5 rounded-full" style={{ border: "2px solid var(--mm-gold)", position: "relative" }}>
              <div className="absolute inset-1 rounded-full" style={{ backgroundColor: "var(--mm-gold)" }} />
            </div>
          </button>
          <button onClick={nextWeek} className="p-2 rounded-lg transition-colors hover:bg-white/10" data-testid="cal-next"><ChevronRight size={20} style={{ color: "var(--mm-text-secondary)" }} /></button>
        </div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>{headerText}</p>

      {/* Week Grid */}
      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 pb-2">
        <div className="flex gap-2 sm:grid sm:grid-cols-7 sm:gap-2 min-w-[700px] sm:min-w-0">
          {weekDates.map((date, i) => {
            const key = formatDateKey(date);
            const isToday = key === today;
            const dayEvents = getEventsForDay(key, i);
            return (
              <div
                key={key}
                className="flex-shrink-0 w-[140px] sm:w-auto rounded-lg p-3 sm:p-4 transition-all"
                style={{
                  backgroundColor: isToday ? "rgba(47, 216, 168, 0.08)" : "rgba(255,255,255,0.02)",
                  border: isToday ? "1px solid rgba(47, 216, 168, 0.3)" : "1px solid var(--mm-border)",
                  minHeight: "130px"
                }}
                data-testid={`cal-day-${key}`}
              >
                <div className="text-center mb-3">
                  <p className="text-xs sm:text-sm font-semibold" style={{ color: isToday ? "var(--mm-gold)" : "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>{DAYS[i]}</p>
                  <p className="text-xl font-bold" style={{ color: isToday ? "var(--mm-gold)" : "var(--mm-text-primary)", fontFamily: "'Space Grotesk', sans-serif" }}>{date.getDate()}</p>
                </div>
                <div className="space-y-2">
                  {dayEvents.map(ev => {
                    const c = ev.color || "#2FD8A8";
                    return (
                    <div
                      key={ev.id}
                      className="rounded-lg px-2.5 py-2 group relative"
                      style={{
                        backgroundColor: `${c}18`,
                        border: `1px solid ${c}40`
                      }}
                      data-testid={`cal-event-${ev.id}`}
                    >
                      <div className="flex items-center gap-1 mb-0.5">
                        {getRecurrence(ev) !== "none" && <Repeat size={10} style={{ color: c, flexShrink: 0 }} />}
                        {ev.time && <span className="text-xs sm:text-sm font-bold" style={{ color: c, fontFamily: "'Space Grotesk', sans-serif" }}>{ev.time}</span>}
                      </div>
                      <p className="font-semibold text-xs sm:text-sm leading-snug" style={{ color: "var(--mm-text-primary)" }}>{ev.title}</p>
                      {ev.educator && <p className="text-xs mt-0.5" style={{ color: "var(--mm-text-secondary)" }}>{ev.educator}</p>}
                      {isAdmin && (
                        <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => startEdit(ev, e)} className="p-0.5 rounded" style={{ backgroundColor: "rgba(47,216,168,0.3)" }}><Edit3 size={10} style={{ color: "var(--mm-gold)" }} /></button>
                          <button onClick={(e) => deleteEvent(ev.id, e)} className="p-0.5 rounded" style={{ backgroundColor: "rgba(239,68,68,0.3)" }}><Trash2 size={10} style={{ color: "#EF4444" }} /></button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                {/* Eventi economici USD (alto impatto + festività) */}
                {getEconForDay(key).length > 0 && (
                  <div className="space-y-1.5 mt-2">
                    {getEconForDay(key).map((ev, idx) => {
                      const holiday = ev.impact === "Holiday";
                      const c = holiday ? "#A78BFA" : "#EF4444";
                      return (
                        <button
                          key={`${ev.title}-${idx}`}
                          onClick={() => setEconPopup(ev)}
                          className="w-full text-left rounded-lg px-2 py-1.5 transition-colors hover:brightness-125"
                          style={{ backgroundColor: `${c}14`, border: `1px dashed ${c}50` }}
                          data-testid={`cal-econ-${key}-${idx}`}
                        >
                          <div className="flex items-center gap-1 mb-0.5">
                            {holiday ? <Landmark size={10} style={{ color: c, flexShrink: 0 }} /> : <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: c }} />}
                            <span className="text-[10px] font-bold uppercase" style={{ color: c, fontFamily: "'Space Grotesk', sans-serif" }}>
                              {holiday ? "Festività USA" : `USD · ${new Date(ev.date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`}
                            </span>
                          </div>
                          <p className="text-xs leading-snug truncate" style={{ color: "var(--mm-text-secondary)" }}>{ev.title}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
                {isAdmin && (
                  <button onClick={() => openAdd(key, i)} className="w-full mt-1.5 p-1 rounded text-xs flex items-center justify-center gap-1 transition-colors hover:bg-white/10" style={{ color: "var(--mm-text-muted)" }} data-testid={`cal-add-${key}`}>
                    <Plus size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add/Edit Event Form */}
      {(showAdd || editingId) && (
        <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: "var(--mm-surface)", border: "1px solid var(--mm-border)" }} data-testid="cal-event-form">
          <h3 className="text-base font-semibold mb-3" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>
            {editingId ? "Modifica Evento" : "Nuovo Evento"}
          </h3>

          {/* Recurrence selector */}
          <div className="mb-4">
            <label className="text-xs mb-1.5 block" style={{ color: "var(--mm-text-muted)" }}>Ripetizione</label>
            <div className="flex flex-wrap gap-2">
              {RECURRENCE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setForm({ ...form, recurrence: opt.value })}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    backgroundColor: form.recurrence === opt.value ? "rgba(100, 200, 255, 0.15)" : "rgba(255,255,255,0.05)",
                    border: form.recurrence === opt.value ? "1px solid rgba(100, 200, 255, 0.4)" : "1px solid var(--mm-border)",
                    color: form.recurrence === opt.value ? "#64C8FF" : "var(--mm-text-secondary)",
                    fontFamily: "'Space Grotesk', sans-serif"
                  }}
                  data-testid={`cal-recurrence-${opt.value}`}
                >
                  {opt.value !== "none" && <Repeat size={13} />}
                  {opt.label}
                </button>
              ))}
            </div>
            {form.recurrence === "weekly" && <span className="text-xs mt-1.5 block" style={{ color: "#64C8FF" }}>Si ripete ogni settimana nel giorno scelto</span>}
            {form.recurrence === "biweekly" && <span className="text-xs mt-1.5 block" style={{ color: "#64C8FF" }}>Si ripete ogni 2 settimane a partire dalla data scelta, stesso giorno e ora</span>}
            {form.recurrence === "monthly" && <span className="text-xs mt-1.5 block" style={{ color: "#64C8FF" }}>Si ripete ogni mese lo stesso giorno del mese della data scelta</span>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="admin-input" placeholder="Nome call" data-testid="cal-form-title" />
            <input type="text" value={form.educator} onChange={(e) => setForm({ ...form, educator: e.target.value })} className="admin-input" placeholder="Educatore" data-testid="cal-form-educator" />

            {/* Color picker */}
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: "var(--mm-text-muted)" }}>Colore</label>
              <div className="flex gap-2">
                {EVENT_COLORS.map(c => (
                  <button
                    key={c.value}
                    onClick={() => setForm({ ...form, color: c.value })}
                    className="w-7 h-7 rounded-full transition-transform"
                    style={{
                      backgroundColor: c.value,
                      border: form.color === c.value ? "3px solid white" : "3px solid transparent",
                      transform: form.color === c.value ? "scale(1.25)" : "scale(1)",
                      boxShadow: form.color === c.value ? `0 0 8px ${c.value}80` : "none"
                    }}
                    title={c.label}
                    data-testid={`cal-color-${c.value}`}
                  />
                ))}
              </div>
            </div>

            {form.recurrence === "weekly" ? (
              <div>
                <label className="text-xs mb-1 block" style={{ color: "var(--mm-text-muted)" }}>Giorno della settimana</label>
                <div className="flex gap-1.5">
                  {DAYS.map((day, idx) => (
                    <button
                      key={idx}
                      onClick={() => setForm({ ...form, dayOfWeek: idx })}
                      className="flex-1 py-2 rounded-lg text-xs font-bold transition-all"
                      style={{
                        backgroundColor: form.dayOfWeek === idx ? "rgba(100, 200, 255, 0.2)" : "rgba(255,255,255,0.03)",
                        border: form.dayOfWeek === idx ? "1px solid #64C8FF" : "1px solid var(--mm-border)",
                        color: form.dayOfWeek === idx ? "#64C8FF" : "var(--mm-text-muted)",
                        fontFamily: "'Space Grotesk', sans-serif"
                      }}
                      data-testid={`cal-day-btn-${idx}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                {form.recurrence !== "none" && (
                  <label className="text-xs mb-1 block" style={{ color: "var(--mm-text-muted)" }}>Data di inizio</label>
                )}
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="admin-input w-full" data-testid="cal-form-date" />
              </div>
            )}

            <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="admin-input" data-testid="cal-form-time" />
          </div>
          <div className="flex gap-3">
            <button onClick={saveEvent} className="btn-primary flex-1 flex items-center justify-center gap-2 py-2"><Save size={16} /> Salva</button>
            <button onClick={() => { setShowAdd(false); setEditingId(null); }} className="btn-secondary flex-1 py-2">Annulla</button>
          </div>
        </div>
      )}

      {/* Popup dettaglio evento economico */}
      {econPopup && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(3px)" }} onClick={() => setEconPopup(null)} data-testid="econ-popup-overlay">
          <div className="w-full max-w-sm p-6" style={{ backgroundColor: "#0b1512", border: `1px solid ${econPopup.impact === "Holiday" ? "#A78BFA" : "#EF4444"}` }} onClick={(e) => e.stopPropagation()} data-testid="econ-event-popup">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                {econPopup.impact === "Holiday"
                  ? <Landmark size={18} style={{ color: "#A78BFA" }} />
                  : <AlertCircle size={18} style={{ color: "#EF4444" }} />}
                <span className="text-xs font-bold uppercase px-2 py-0.5" style={{
                  color: econPopup.impact === "Holiday" ? "#A78BFA" : "#EF4444",
                  backgroundColor: econPopup.impact === "Holiday" ? "rgba(167,139,250,0.12)" : "rgba(239,68,68,0.12)",
                  fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.08em"
                }}>
                  {econPopup.impact === "Holiday" ? "Festività USA" : "USD · Impatto Alto"}
                </span>
              </div>
              <button onClick={() => setEconPopup(null)} className="p-1 transition-colors hover:bg-white/10" data-testid="econ-popup-close">
                <X size={16} style={{ color: "var(--mm-text-muted)" }} />
              </button>
            </div>
            <h3 className="text-base font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }} data-testid="econ-popup-title">{econPopup.title}</h3>
            <p className="text-xs mb-3" style={{ color: "var(--mm-text-muted)" }}>
              {new Date(econPopup.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
              {econPopup.impact !== "Holiday" && ` · ore ${new Date(econPopup.date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`}
            </p>
            <p className="text-sm mb-4" style={{ color: "var(--mm-text-secondary)", lineHeight: 1.6 }} data-testid="econ-popup-desc">
              {econPopup.impact === "Holiday"
                ? "Le banche americane sono chiuse. Aspettati bassa liquidità, spread più ampi e possibili movimenti anomali: valuta di ridurre l'operatività su USD e XAUUSD."
                : "Dato macro USA ad alto impatto: attesa forte volatilità su USD e XAUUSD nei minuti attorno all'uscita. Gestisci il rischio e fai attenzione agli spread."}
            </p>
            {(econPopup.forecast || econPopup.previous) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2.5 text-center" style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid var(--mm-border)" }}>
                  <p className="text-[10px] uppercase" style={{ color: "var(--mm-text-muted)" }}>Previsione</p>
                  <p className="text-sm font-bold font-mono" style={{ color: "var(--mm-text-primary)" }}>{econPopup.forecast || "—"}</p>
                </div>
                <div className="p-2.5 text-center" style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid var(--mm-border)" }}>
                  <p className="text-[10px] uppercase" style={{ color: "var(--mm-text-muted)" }}>Precedente</p>
                  <p className="text-sm font-bold font-mono" style={{ color: "var(--mm-text-primary)" }}>{econPopup.previous || "—"}</p>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Admin quick add */}
      {isAdmin && !showAdd && !editingId && (
        <button onClick={() => openAdd()} className="mt-3 btn-primary flex items-center gap-2 mx-auto text-sm py-2 px-4" data-testid="cal-add-event-btn">
          <Plus size={16} /> Aggiungi Call
        </button>
      )}
    </div>
  );
}
